/**
 * The application shell.
 *
 * **The chrome is shared and whole** (FiveOX ADR-0037). `<PlatformShell>` is
 * a top bar, a left sidebar and the content between them; this app renders it
 * and composes nothing of its own around it. In particular:
 *
 * - The nav bar carries **no app navigation**. It is suite chrome: brand, org,
 *   notifications, app-switcher waffle, account menu.
 * - TDS Reco's navigation goes in the **sidebar, as data** — `NavItem[]` with at
 *   most one level of `children`.
 * - The bell is the shell's, mounted with **our** authenticated fetcher, not a
 *   token: each app owns a different session store with its own idea of what a
 *   dead session means, and the bell must not become one more place that
 *   decides.
 * - **No router import inside the package.** `renderLink` wraps each item in
 *   react-router's `<Link>`; without one the sidebar renders plain anchors.
 */

import {
  NoAccess,
  Notifications,
  PlatformShell,
  signOutEverywhere,
  useSwitcherApps,
} from '@fiveox/shell'
import type { NavItem } from '@fiveox/shell'
import { Building2, LayoutDashboard } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router'

import { CLIENT, useEvaluation } from './lib/access'
import type { Verb } from './lib/access'
import { getOrgId, setOrgId } from './lib/api'
import { useOrgs } from './lib/use-orgs'
import {
  ACCESS_API,
  ACCOUNTS_URL,
  ADMIN_URL,
  APPMGMT_API,
  APP_NAME,
  APP_SLUG,
  AUTH_API,
  COMM_API,
  CONSOLE_URL,
  CRM_URL,
  HELP_URL,
  LAUNCHER_URL,
  STORE_URL,
  SUPPORT_URL,
} from './lib/config'
import { ToastHost } from './components/toast'
import { useMe } from './lib/use-me'
import { session } from './lib/session'
import { Paywall } from './routes/Paywall'

/**
 * The sidebar, as data.
 *
 * `needs` is ours, not the shell's: `[resource, verb]`, matching the matrix
 * access-management actually speaks. An item whose verb the user does not hold
 * is **dropped**, not disabled — a disabled item advertises a capability the
 * user cannot obtain and generates a support ticket.
 */
const NAV: Array<NavItem & { needs?: [string, Verb] }> = [
  { key: 'overview', label: 'Overview', href: '/', icon: <LayoutDashboard size={18} />, end: true },
  {
    key: 'clients',
    label: 'Clients',
    href: '/clients',
    icon: <Building2 size={18} />,
    needs: [CLIENT, 'can_view'],
  },
]

// P7 note: the placeholder `uploads` / `reconciliation` sidebar entries are
// gone — those surfaces live INSIDE the client workspace
// (`/w/:clientId/:periodId/*`, workspace/Workspace.tsx), because an upload or
// a reconciliation is always about one client's one FY. The sidebar carries
// what is client-agnostic; the workspace tab bar carries the rest.

export function App({ children }: { children: ReactNode }) {
  const current = session.useSession()
  const location = useLocation()
  const me = useMe()
  // Resolve the org *before* anything asks for permissions: every request to
  // tdsreco-service carries it, and `useEvaluation` is one of them.
  const { orgs, selected, loading: orgsLoading, failed: orgsFailed } = useOrgs()
  const { evaluation, loading, can } = useEvaluation()

  // **The waffle is identity-shaped, not app-shaped** (ADR-0054): an internal
  // caller is offered the internal apps their platform-scoped assignments
  // grant; an external caller gets the customer estate. One resolver, all apps.
  //
  // This used to be a hardcoded three-entry literal with our own wording —
  // "Console" where the estate says "Organisation", "Help" where it says
  // "Support" — and `accounts` missing altogether. `platformApps` owns the
  // canonical names and order; entries with no URL are dropped rather than
  // rendered dead.
  const switcherApps = useSwitcherApps({
    accessApiUrl: ACCESS_API,
    accessToken: current?.accessToken ?? null,
    staffUrls: { 'core-admin': ADMIN_URL, 'core-crm': CRM_URL, 'core-support': SUPPORT_URL },
    platformUrls: {
      accounts: ACCOUNTS_URL,
      console: CONSOLE_URL,
      store: STORE_URL,
      help: HELP_URL,
    },
    accountsUrl: ACCOUNTS_URL,
    // The org's own entitled apps ride ahead of the platform surfaces
    // (core-launcher T2.1a·2) — launchable only, from appmgmt's
    // `GET /me/apps`. Our fetcher, not a bare token: `session.authed`
    // refreshes a spent access token and replays, same as the bell.
    appmgmtApiUrl: APPMGMT_API,
    orgId: selected?.id ?? null,
    fetcher: session.authed,
  })

  const signOut = () => {
    void signOutEverywhere({
      authApiUrl: AUTH_API,
      refreshToken: current?.refreshToken ?? null,
      clearLocal: session.clearSession,
    })
  }

  if (orgsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Loading your organisations…</p>
      </div>
    )
  }

  // Org-service did not answer. **Not** the same as belonging to no org, and
  // the copy must not say it is — telling someone they have no organisation
  // when the service is merely down sends them to support with the wrong
  // question.
  if (orgsFailed) {
    return (
      <NoAccess
        launcherUrl={LAUNCHER_URL}
        onSwitchAccount={signOut}
        title="We can’t reach your organisations"
        message="The organisation service did not answer. This is usually brief — please try again in a moment."
      />
    )
  }

  // Genuinely a member of nothing. A real state for a brand-new account, and
  // the only way out is to create or join one — which is accounts' job, not
  // ours.
  if (!selected || !getOrgId()) {
    return (
      <NoAccess
        launcherUrl={LAUNCHER_URL}
        onSwitchAccount={signOut}
        title="You’re not in an organisation yet"
        message="TDS Reco works inside an organisation — that is who holds the subscription and the client list. Create one from your account, then come back."
      />
    )
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Checking your access…</p>
      </div>
    )
  }

  // An empty verb set is the single answer for every lapse — never bought,
  // subscription suspended, app killed, authority unreachable. Enforcing
  // `permissions` alone is entitlement-correct (guide §7.3), and the *reason*
  // is what lets the screen say why.
  if (!evaluation || Object.keys(evaluation.permissions).length === 0) {
    return (
      <Paywall
        reason={evaluation?.entitlementReason ?? 'authority_unreachable'}
        orgs={orgs}
        selectedOrgId={selected.id}
        onSelectOrg={setOrgId}
      />
    )
  }

  const items: NavItem[] = NAV.filter(
    (item) => !item.needs || can(item.needs[0], item.needs[1]),
  ).map(({ needs: _needs, ...item }) => item)

  return (
    <PlatformShell
      appName={APP_NAME}
      appSlug={APP_SLUG}
      launcherUrl={LAUNCHER_URL}
      account={{
        name: me?.name ?? null,
        email: me?.email ?? null,
        pictureUrl: me?.pictureUrl ?? null,
        accountsUrl: ACCOUNTS_URL,
        onSignOut: signOut,
      }}
      apps={switcherApps}
      // The org switcher. One prop, two behaviours: with `options` and
      // `onSelect` it switches; with only `selected` it renders a read-only
      // chip — which is what a single-org user gets, correctly.
      org={{
        selected: { id: selected.id, display_name: selected.display_name, role: selected.role },
        options: orgs.length > 1 ? orgs : undefined,
        onSelect: orgs.length > 1 ? (id: string) => setOrgId(id) : undefined,
      }}
      notifications={<Notifications commApiUrl={COMM_API} fetcher={session.authed} />}
      nav={{
        items,
        currentPath: location.pathname,
        renderLink: (item, className) => (
          <Link to={item.href} className={className}>
            {item.icon}
            {item.label}
          </Link>
        ),
      }}
      contentClassName="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6"
    >
      {children}
      {/* the estate has no shared notification system yet; the ported
          screens' toasts render here (components/toast.tsx) */}
      <ToastHost />
    </PlatformShell>
  )
}
